import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  GraduationCap,
  MessageSquare,
  CalendarCheck,
  GitBranch,
  Boxes,
  Award,
  Grid3X3,
  CheckCircle,
  Sparkles,
  ArrowRight,
  Zap,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

// Tool configurations with role-based visibility
// roles: undefined = all roles, or array of allowed roles
const TOOLS = [
  { id: 'spaces', name: 'Spaces', description: 'Manage your workspaces', icon: Boxes, route: '/dashboard', color: '#6366f1', roles: ['Admin', 'Manager'] },
  { id: 'users', name: 'Users', description: 'Team management', icon: Users, route: '/company-team', color: '#10b981', roles: ['Admin'] },
  { id: 'matrix', name: 'Matrix', description: 'Company-wide training', icon: Grid3X3, route: '/matrix', color: '#8b5cf6', roles: ['Admin'] },
  { id: 'qualifications', name: 'Qualifications', description: 'Skills & certifications', icon: Award, route: '/qualifications', color: '#f59e0b', roles: ['Admin'] },
  { id: 'trainers', name: 'Trainers', description: 'Training providers', icon: GraduationCap, route: '/trainers', color: '#3b82f6', roles: ['Admin'] },
  { id: 'attendance', name: 'Attendance', description: 'Leave & shifts', icon: CalendarCheck, route: '/attendance', color: '#8b5cf6' }, // All roles
  { id: 'posts', name: 'Posts', description: 'Announcements', icon: MessageSquare, route: '/posts', color: '#ec4899' }, // All roles
  { id: 'docs', name: 'Documents', description: 'Files & policies', icon: FileText, route: '/docs', color: '#0ea5e9' }, // All roles
  { id: 'processes', name: 'Processes', description: 'Workflows & automation', icon: GitBranch, route: '/processes', color: '#14b8a6', roles: ['Admin'] },
];

export const GlobalToolsExpandingPanel = ({ isOpen, onClose, currentTool }) => {
  const navigate = useNavigate();
  const { userRole } = useAuth();

  // Filter tools based on user role
  const visibleTools = useMemo(() => {
    return TOOLS.filter(tool => {
      // If no roles specified, tool is visible to all
      if (!tool.roles) return true;
      // Otherwise, check if user's role is in the allowed roles
      return tool.roles.includes(userRole);
    });
  }, [userRole]);

  const handleToolClick = (tool) => {
    navigate(tool.route);
    onClose();
  };

  return (
    <div
      className={`grid transition-all duration-300 ease-out bg-gradient-to-b from-gray-50 to-white ${isOpen ? 'border-b border-gray-200' : ''}`}
      style={{
        gridTemplateRows: isOpen ? '1fr' : '0fr',
      }}
    >
      <div className="overflow-hidden">
        <div className="container mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Tools Grid - Takes 2 columns on large screens */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Tools</h3>
                <span className="text-xs text-gray-500">Navigate your workspace</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {visibleTools.map((tool) => {
                  const Icon = tool.icon;
                  const isActive = currentTool === tool.id;

                  return (
                    <button
                      key={tool.id}
                      onClick={() => handleToolClick(tool)}
                      className={`group relative flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
                        isActive
                          ? 'bg-white border-gray-300 shadow-sm'
                          : 'bg-white/50 border-gray-200 hover:bg-white hover:border-gray-300'
                      }`}
                    >
                      {isActive && (
                        <CheckCircle
                          className="absolute top-2 right-2 h-4 w-4 text-green-500"
                          strokeWidth={2.5}
                        />
                      )}
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg transition-transform group-hover:scale-110"
                        style={{ backgroundColor: `${tool.color}15` }}
                      >
                        <Icon className="h-5 w-5" style={{ color: tool.color }} />
                      </div>
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-900 block">{tool.name}</span>
                        <span className="text-xs text-gray-500 hidden sm:block">{tool.description}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Featured Section - Takes 1 column (Admin only for "What's New") */}
            <div className="lg:col-span-1">
              {userRole === 'Admin' && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <h3 className="text-sm font-semibold text-gray-900">What's New</h3>
                  </div>

                  {/* Featured Card */}
                  <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-5 text-white">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="h-5 w-5" />
                      <span className="text-xs font-medium uppercase tracking-wider opacity-90">New Feature</span>
                    </div>
                    <h4 className="text-lg font-semibold mb-2">Workflow Automation</h4>
                    <p className="text-sm opacity-90 mb-4">
                      Create automated workflows to streamline your training processes and save hours every week.
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="bg-white/20 hover:bg-white/30 text-white border-0"
                      onClick={() => {
                        navigate('/processes');
                        onClose();
                      }}
                    >
                      Learn More
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </>
              )}

              {/* Quick Tip - visible to all */}
              <div className={`p-4 bg-amber-50 border border-amber-200 rounded-xl ${userRole === 'Admin' ? 'mt-4' : ''}`}>
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-amber-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-900">Pro Tip</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Press <kbd className="px-1.5 py-0.5 bg-amber-100 rounded text-amber-800 font-mono">Ctrl+K</kbd> to quickly search and navigate anywhere.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
